import React, { useState } from 'react';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { Link } from 'wouter';
import { ChevronLeft, ChevronRight, Briefcase, Coins, Building, UserPlus, Users, Lightbulb, PanelRight, X, Share2, Clock, Lock } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';

export default function PracticeModelsLecture() {
  const [activeTab, setActiveTab] = useState('overview');
  const [showToc, setShowToc] = useState(false);

  // Table of Contents
  const tocItems = [
    { id: 'intro', label: 'Introduction', offset: 0 },
    { id: 'concierge', label: 'Concierge Practice Model', offset: 200 },
    { id: 'hybrid', label: 'Hybrid Practice Model', offset: 500 },
    { id: 'corporate', label: 'Corporate Wellness Model', offset: 800 },
    { id: 'revenue', label: 'Revenue Streams', offset: 1100 },
    { id: 'case-studies', label: 'Case Studies', offset: 1400 },
  ];

  const scrollToSection = (offset: number) => {
    window.scrollTo({
      top: offset,
      behavior: 'smooth'
    });
    setShowToc(false);
  };

  return (
    <MentorshipAccessGate>
    <div className="min-h-screen flex flex-col relative bg-white">
      <Navbar />
      
      {/* Mobile TOC button */}
      <button
        onClick={() => setShowToc(!showToc)}
        className="md:hidden fixed bottom-6 right-6 z-40 bg-indigo-600 text-white p-3 rounded-full shadow-lg"
      >
        {showToc ? <X size={24} /> : <PanelRight size={24} />}
      </button>
      
      {/* Mobile TOC sidebar */}
      <div className={cn(
        "md:hidden fixed inset-y-0 right-0 z-30 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out pt-16",
        showToc ? "translate-x-0" : "translate-x-full"
      )}>
        <div className="p-4">
          <h3 className="font-bold text-lg mb-4 text-gray-900">Table of Contents</h3>
          <ul className="space-y-3">
            {tocItems.map((item, index) => (
              <li key={index}>
                <button
                  onClick={() => scrollToSection(item.offset)}
                  className="text-left text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <main className="flex-1 container mx-auto px-4 py-8 md:flex md:gap-8">
        {/* Desktop TOC sidebar */}
        <aside className="hidden md:block w-64 flex-shrink-0">
          <div className="sticky top-24">
            <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
              <h3 className="font-bold text-lg mb-4 text-gray-900">Table of Contents</h3>
              <ul className="space-y-3">
                {tocItems.map((item, index) => (
                  <li key={index}>
                    <button
                      onClick={() => scrollToSection(item.offset)}
                      className="text-left text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>
        
        {/* Main content */}
        <div className="flex-1 mt-6 md:mt-0">
          <div className="mb-8">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <Link href="/mentorship" className="hover:text-indigo-600">Mentorship</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/mentorship/business" className="hover:text-indigo-600">Business Lectures</Link>
              <ChevronRight className="h-4 w-4" />
              <span className="text-gray-900">Practice Models</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 font-serif mb-4">Practice Models & Revenue Streams for Longevity Medicine</h1>
            
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center text-gray-600">
                <Clock className="h-4 w-4 mr-1" />
                <span className="text-sm">45 min lecture</span>
              </div>
              <div className="flex items-center text-gray-600">
                <Share2 className="h-4 w-4 mr-1" />
                <span className="text-sm">Mentorship Exclusive</span>
              </div>
            </div>
            
            <p className="text-lg text-gray-700 mb-6">
              Learn how to build a sustainable and profitable longevity medicine practice with different practice models
              and revenue streams that align with your goals and target patient population. This comprehensive lecture covers
              everything from concierge models to hybrid approaches, with real-world case studies and financial projections.
            </p>
          </div>
          
          <Tabs defaultValue="overview" className="mb-12" onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="slides">Slides</TabsTrigger>
              <TabsTrigger value="resources">Resources</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="pt-6">
              <div className="prose prose-lg max-w-none">
                <section id="intro" className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 font-serif">Introduction to Longevity Medicine Practice Models</h2>
                  <p>
                    In today's rapidly evolving healthcare landscape, longevity medicine represents one of the most promising frontiers for both patient outcomes and practice development. Unlike traditional medical specialties that focus on treating disease, longevity medicine emphasizes prevention, optimization, and extending healthspan. This fundamental difference requires careful consideration when selecting your practice model.
                  </p>
                  <p>
                    The three primary models we'll explore are:
                  </p>
                  <ul className="space-y-2 my-4">
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-0.5">
                        <Briefcase className="h-3.5 w-3.5 text-indigo-600" />
                      </div>
                      <span><strong>Concierge Model</strong> - Subscription-based practice with limited patient panels</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-0.5">
                        <Building className="h-3.5 w-3.5 text-indigo-600" />
                      </div>
                      <span><strong>Hybrid Model</strong> - Combining insurance and cash-pay services</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-indigo-100 flex items-center justify-center mr-3 mt-0.5">
                        <Users className="h-3.5 w-3.5 text-indigo-600" />
                      </div>
                      <span><strong>Corporate Wellness Model</strong> - B2B approach targeting employers</span>
                    </li>
                  </ul>
                  <p>
                    Each model offers distinct advantages and challenges, and selecting the right one depends on your target patient demographics, geographic location, personal practice philosophy, and financial goals.
                  </p>
                </section>
                
                <section id="concierge" className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 font-serif">The Concierge Practice Model</h2>
                  <p>
                    The concierge model has gained significant traction in longevity medicine due to its alignment with the personalized, high-touch nature of longevity care. In this model, patients pay a membership fee (typically monthly or annually) in exchange for enhanced access, longer appointments, and a suite of premium services.
                  </p>
                  
                  <h3 className="text-xl font-bold mt-6 mb-3">Key Features:</h3>
                  <ul className="space-y-2 my-4">
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                        <UserPlus className="h-3.5 w-3.5 text-green-600" />
                      </div>
                      <span><strong>Limited Patient Panel</strong> - Typically 300-600 patients per physician</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                        <Clock className="h-3.5 w-3.5 text-green-600" />
                      </div>
                      <span><strong>Extended Appointment Times</strong> - 30-60 minute appointments vs. 15 minutes in traditional practices</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                        <Coins className="h-3.5 w-3.5 text-green-600" />
                      </div>
                      <span><strong>Membership Fees</strong> - Ranging from $150-500/month for basic services</span>
                    </li>
                  </ul>
                  
                  <div className="bg-blue-50 p-4 rounded-lg my-6 border border-blue-100">
                    <h4 className="font-bold text-blue-800 mb-2">Case Study: Dr. Sarah's Premium Longevity Practice</h4>
                    <p className="text-blue-900">
                      Dr. Sarah transitioned from a traditional internal medicine practice to a concierge longevity model with 350 patients each paying $300/month. This generated $1.26M in annual recurring revenue while allowing for comprehensive care plans, advanced biomarker testing, and quarterly in-depth health reviews.
                    </p>
                  </div>
                  
                  <h3 className="text-xl font-bold mt-6 mb-3">Financial Considerations:</h3>
                  <p>
                    With 400 patients at an average of $250/month, a concierge practice can generate $1.2M in annual revenue. Operating expenses typically range from 45-60% of revenue, including:
                  </p>
                  <ul className="list-disc pl-6 my-4 space-y-1">
                    <li>Staff salaries (medical assistant, patient coordinator)</li>
                    <li>Office lease (~$5,000-8,000/month for 1,500-2,000 sq ft)</li>
                    <li>Electronic health record system (~$500-800/month)</li>
                    <li>Liability insurance (~$20,000-30,000/year)</li>
                    <li>Marketing (typically 5-8% of revenue)</li>
                  </ul>
                </section>
                
                <section id="hybrid" className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 font-serif">The Hybrid Practice Model</h2>
                  <p>
                    The hybrid model combines elements of traditional insurance-based care with cash-pay premium services. This approach allows physicians to maintain a broader patient base while offering advanced longevity services to interested patients.
                  </p>
                  
                  <h3 className="text-xl font-bold mt-6 mb-3">Key Features:</h3>
                  <ul className="space-y-2 my-4">
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-0.5">
                        <Coins className="h-3.5 w-3.5 text-purple-600" />
                      </div>
                      <span><strong>Tiered Service Offerings</strong> - Basic care (insurance-based) with premium add-on services</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-0.5">
                        <Users className="h-3.5 w-3.5 text-purple-600" />
                      </div>
                      <span><strong>Larger Patient Panel</strong> - 1,000-2,000 patients with varied engagement levels</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center mr-3 mt-0.5">
                        <Lightbulb className="h-3.5 w-3.5 text-purple-600" />
                      </div>
                      <span><strong>Flexible Conversion Path</strong> - Allows patients to "sample" advanced care before committing</span>
                    </li>
                  </ul>
                  
                  <p>
                    This model works particularly well for physicians transitioning from traditional practice to longevity medicine, allowing for a gradual shift while maintaining stable revenue.
                  </p>
                </section>
                
                <section id="corporate" className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 font-serif">The Corporate Wellness Model</h2>
                  <p>
                    The corporate wellness model focuses on partnering with employers to provide longevity and preventive health services to employees. This B2B approach can scale quickly and targets companies looking to reduce healthcare costs and improve employee performance through preventive health measures.
                  </p>
                  
                  <h3 className="text-xl font-bold mt-6 mb-3">Key Features:</h3>
                  <ul className="space-y-2 my-4">
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-0.5">
                        <Building className="h-3.5 w-3.5 text-amber-600" />
                      </div>
                      <span><strong>Contract-Based Revenue</strong> - Annual or multi-year contracts with corporations</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-0.5">
                        <Users className="h-3.5 w-3.5 text-amber-600" />
                      </div>
                      <span><strong>Volume Approach</strong> - Serving larger groups with some standardized protocols</span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-0.5">
                        <Coins className="h-3.5 w-3.5 text-amber-600" />
                      </div>
                      <span><strong>ROI-Focused</strong> - Demonstrating healthcare cost reduction and productivity improvements</span>
                    </li>
                  </ul>
                </section>
                
                <section id="revenue" className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 font-serif">Revenue Streams in Longevity Medicine</h2>
                  <p>
                    Regardless of your primary practice model, diversifying revenue streams is essential for practice stability and growth. Successful longevity practices typically combine multiple revenue sources:
                  </p>
                  
                  <div className="overflow-x-auto my-6">
                    <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="py-3 px-4 border-b text-left">Revenue Stream</th>
                          <th className="py-3 px-4 border-b text-left">Typical Pricing</th>
                          <th className="py-3 px-4 border-b text-left">Implementation Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="py-3 px-4 border-b font-medium">Membership Fees</td>
                          <td className="py-3 px-4 border-b">$150-500/month</td>
                          <td className="py-3 px-4 border-b">Recurring revenue base; consider tiered membership levels</td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 border-b font-medium">Comprehensive Assessments</td>
                          <td className="py-3 px-4 border-b">$1,500-5,000</td>
                          <td className="py-3 px-4 border-b">Annual deep-dive evaluations with advanced biomarkers</td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 border-b font-medium">Peptide & Supplementation</td>
                          <td className="py-3 px-4 border-b">20-40% markup</td>
                          <td className="py-3 px-4 border-b">In-office dispensing with appropriate markup</td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 border-b font-medium">Advanced Procedures</td>
                          <td className="py-3 px-4 border-b">$400-2,500/procedure</td>
                          <td className="py-3 px-4 border-b">Exosomes, PRP, IV therapies, etc.</td>
                        </tr>
                        <tr>
                          <td className="py-3 px-4 border-b font-medium">Group Programs</td>
                          <td className="py-3 px-4 border-b">$500-2,000/program</td>
                          <td className="py-3 px-4 border-b">8-12 week guided health optimization programs</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>
                
                <section id="case-studies" className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 font-serif">Case Studies: Successful Practice Implementations</h2>
                  
                  <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-lg my-6 border border-indigo-100">
                    <h3 className="font-bold text-indigo-800 mb-3 text-xl">Case Study 1: Urban Concierge Practice</h3>
                    <p className="text-indigo-900 mb-4">
                      Dr. Michael established a concierge longevity practice in San Francisco with 300 patients at $400/month, generating $1.44M annually. His practice focuses on executives and tech professionals, offering:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-indigo-900 mb-4">
                      <li>Quarterly advanced biomarker panels</li>
                      <li>Continuous glucose monitoring interpretation</li>
                      <li>Peptide protocols for performance optimization</li>
                      <li>Executive health assessments with epigenetic testing</li>
                    </ul>
                    <p className="text-indigo-900">
                      <strong>Key Success Factor:</strong> Focusing on a specific demographic allowed for tailored services and targeted marketing, resulting in a 92% patient retention rate.
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-lg my-6 border border-purple-100">
                    <h3 className="font-bold text-purple-800 mb-3 text-xl">Case Study 2: Suburban Hybrid Practice</h3>
                    <p className="text-purple-900 mb-4">
                      Dr. Jennifer transitioned her traditional family practice to a hybrid model with 1,500 patients. 200 patients enrolled in her premium longevity program ($250/month), while others remained in the insurance-based practice with à la carte longevity services available.
                    </p>
                    <p className="text-purple-900">
                      <strong>Revenue Breakdown:</strong>
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-purple-900 mb-4">
                      <li>Premium membership program: $600,000/year</li>
                      <li>Insurance reimbursements: $450,000/year</li>
                      <li>À la carte services: $300,000/year</li>
                      <li>Supplement/peptide dispensing: $200,000/year</li>
                    </ul>
                    <p className="text-purple-900">
                      <strong>Key Success Factor:</strong> The hybrid approach allowed for steady growth without disrupting existing patient relationships.
                    </p>
                  </div>
                </section>
                
                <div className="border-t border-gray-200 pt-8 mt-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 font-serif">Conclusion</h2>
                  <p>
                    Selecting the right practice model is a critical decision that impacts everything from patient experience to financial sustainability. Most successful longevity medicine practices share several key characteristics:
                  </p>
                  <ul className="list-disc pl-6 my-4 space-y-1">
                    <li>Clear value proposition that justifies premium pricing</li>
                    <li>Multiple complementary revenue streams</li>
                    <li>Operational efficiency through appropriate staffing and technology</li>
                    <li>Consistent marketing to attract ideal patients</li>
                    <li>Documented outcomes to demonstrate effectiveness</li>
                  </ul>
                  <p>
                    Each physician must consider their own strengths, interests, and market conditions when designing their practice model. The most successful practices often evolve over time, adapting their approach based on patient feedback and market response.
                  </p>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="slides" className="pt-6">
              <div className="bg-gray-100 p-8 rounded-lg text-center flex items-center justify-center min-h-[400px]">
                <div>
                  <div className="mb-6">
                    <Briefcase className="h-16 w-16 text-indigo-600 mx-auto" />
                  </div>
                  <h3 className="text-xl font-medium mb-4">Presentation Slides</h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Downloadable slides from this lecture are available in PDF format.
                  </p>
                  <Button className="bg-indigo-600 hover:bg-indigo-700">
                    Download Slides
                  </Button>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="resources" className="pt-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Financial Projection Templates</h3>
                  <p className="text-gray-700 mb-4">
                    Excel templates for financial projections for different practice models, including startup costs, 
                    break-even analysis, and 5-year projections.
                  </p>
                  <Button className="bg-blue-600 hover:bg-blue-700">Download Template</Button>
                </div>
                
                <div className="bg-green-50 rounded-lg p-6 border border-green-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Marketing Playbook</h3>
                  <p className="text-gray-700 mb-4">
                    Comprehensive guide to marketing your longevity practice, including patient 
                    acquisition strategies, messaging examples, and content marketing templates.
                  </p>
                  <Button className="bg-green-600 hover:bg-green-700">Download Playbook</Button>
                </div>
                
                <div className="bg-amber-50 rounded-lg p-6 border border-amber-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Service Package Templates</h3>
                  <p className="text-gray-700 mb-4">
                    Ready-to-use templates for creating membership packages, including service descriptions, 
                    pricing strategies, and patient contracts.
                  </p>
                  <Button className="bg-amber-600 hover:bg-amber-700">Download Templates</Button>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-6 border border-purple-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Recommended Reading</h3>
                  <p className="text-gray-700 mb-4">
                    Curated list of books, articles, and research papers on healthcare business models, 
                    concierge medicine, and building a successful longevity practice.
                  </p>
                  <Button className="bg-purple-600 hover:bg-purple-700">View List</Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="mt-12 border-t border-gray-200 pt-8 flex justify-between">
            <Link href="/mentorship/business">
              <Button variant="outline" className="flex items-center">
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back to Business Lectures
              </Button>
            </Link>
            <Link href="/mentorship/business/marketing">
              <Button className="bg-indigo-600 hover:bg-indigo-700 flex items-center">
                Next Lecture: Marketing
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
    </MentorshipAccessGate>
  );
}